import type {
  AxiosError, AxiosResponse, InternalAxiosRequestConfig,
} from 'axios';
import { onAuthStateChanged } from 'firebase/auth';

import { getValueFromStorage, setValueToStorage } from 'hooks/useLocalStorage';
import { axiosInstance, axiosInstanceFB } from 'services/Api';

import envConfig from 'configuration/env';
import { auth } from 'configuration/firebase';
import { LocalStorageKeys } from 'constants/enums';
import { getApiError } from 'utils/errorUtils';

const { apiBaseUrl } = envConfig;

axiosInstance.defaults.baseURL = apiBaseUrl;
axiosInstanceFB.defaults.baseURL = apiBaseUrl;

let isUnauthorized = false;

const cleanupUnauthorizedCheck = () => {
  isUnauthorized = false;
};

const getUserToken = async () => await new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, async (userResponse) => {
    if (userResponse) {
      const token = await userResponse.getIdToken();
      resolve(token);
    } else {
      resolve(null);
    }

    unsubscribe();
  });
});

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const { idToken } = getValueFromStorage(LocalStorageKeys.jwtToken, null) || {};

  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`;
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
  }

  return config;
};

const onRequestWithFB = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const token = await getUserToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token as string}`;
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
};

const onResponse = (response: AxiosResponse): AxiosResponse => response;

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  const status = error.status || (error.response ? error.response.status : 0);
  let formattedError = getApiError(error);

  if (status === 401) {
    if (!isUnauthorized) {
      isUnauthorized = true;
      setTimeout(cleanupUnauthorizedCheck, 1000);
      setValueToStorage(LocalStorageKeys.jwtToken, null);
      setValueToStorage(LocalStorageKeys.user, null);
      auth.signOut();
    } else {
      formattedError = '';
    }
  }

  if (import.meta.env.VITE_NODE_ENV === 'local') {
    console.error(`[response error]: ${JSON.stringify(formattedError || error)}`);
  }

  return Promise.reject(formattedError);
};

const setupAxiosInterceptors = () => {
  axiosInstance.interceptors.request.use(onRequest);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);

  axiosInstanceFB.interceptors.request.use(onRequestWithFB);
  axiosInstanceFB.interceptors.response.use(onResponse, onResponseError);
};

export default setupAxiosInterceptors;
